import type { PaletteMode } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';
import { createTheme as muiCreateTheme } from '@mui/material/styles';

import CreateMuiComponents from './CreateMuiComponents';
import createPalette from './createPalette';
import createShadows from './createShadows';
import createTypography from './createTypography';
import { applyOpacityToHex, replaceRgbaColor } from './themeUtils';

export const boxShadowsToDropShadows = (boxShadows: string) => {
  const boxShadowsMatches = boxShadows.matchAll?.(/(.+?(?:\([^)]*\))*?)(?:,|$)/g);
  const dropShadows =
    boxShadowsMatches &&
    Array.from(boxShadowsMatches, (match) => (match.length ? match[1] : undefined))
      .filter((boxShadow) => boxShadow?.includes(' '))
      .map((boxShadow) => {
        // convert box-shadow params to drop-shadow filters
        const boxShadowParamsMatches = boxShadow?.matchAll(/(\S+?(?:\(.*?\))?)(?:\s|$)+/g);
        const boxShadowParams = Array.from(boxShadowParamsMatches || [], (match) =>
          match.length ? match[1] : undefined,
        );
        return `drop-shadow(${boxShadowParams
          .filter((param) => param !== 'inset') // drop 'inset'
          .filter((param, index) => index !== 3 || !/^(\+|-)?\d+?.*?$/m.test(`${param}`)) // drop spread-radius
          .join(' ')})`;
      })
      .join(' ');
  return dropShadows || boxShadows;
};

const steps = 20;
const singleStep = 100 / steps;
const halfStep = singleStep / 2;
const bottomCutOffPath = [...Array(steps + 1).keys()]
  .slice(1, steps + 1)
  .reverse()
  .map(
    (step) =>
      `${step * singleStep}% ${100}%, ${step * singleStep - halfStep}% ${100 - halfStep}%, ${
        step * singleStep - halfStep - halfStep
      }% ${100}%`,
  )
  .join(', ');
const bottomCutOffClipPath = `polygon(0 0, 100% 0, 100% 100%, ${bottomCutOffPath}, 0 100%)`;

function createThemeOverrides(mode: PaletteMode): ThemeOptions {
  const palette = createPalette(mode);
  const shadows = createShadows(mode);

  const typography = createTypography();

  return {
    mode,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768, // inserted, default was 960, added xxl
        lg: 960,
        xl: 1280,
        xxl: 1920,
      },
    },
    palette,

    shape: {
      // material-ui only uses border radius

      // TODO: Why do we use these?
      padding: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 70, 78, 86, 94, 102],
      shapesRadius: {
        rounded: '50%',
      },
      bottomCutOffClipPath,
    },

    // TODO: Should we remove shadows and use material-ui default shadows?
    shadows,

    typography,

    // TODO: These should go away!
    //
    project: 'Simplifi',
    featureHeader: {
      titleVariant: 'h5',
      subTitleVariant: 'subtitle1',
    },
    defaults: {
      dense: window.screen.width > 1024,
      buttonHoverBackgroundColor: '#ebebeb',
      content: {
        // -- sizing -- //
        height: '100%',
        width: 960,
        pageMinWidth: 690,
        // -- spacing -- //
        paddingLeft: 24,
        paddingLeftMobile: 16,
        paddingRight: 24,
        paddingRightMobile: 16,
      },
      headers: {
        // -- sizing -- //
        subHeaderHeight: 64,
        subHeaderHeightTall: 75,
        headerHeight: 75,
      },
    },

    applyOpacityToHex,
    replaceRgbaColor,
  };
}

export default function createTheme(mode: PaletteMode) {
  const globalTheme = muiCreateTheme(createThemeOverrides(mode));
  return muiCreateTheme(
    {
      components: {
        ...CreateMuiComponents(globalTheme),
      },
    },
    globalTheme,
  );
}
